/* eslint-disable @typescript-eslint/ban-ts-comment */
// Types
import type { ClientConfig, ConfigOptions, RequestBody } from '@white-label-types/parking-checkout';
import type { AxiosRequestConfig } from 'axios';
import type { Route } from 'vue-router';

// Services
import { httpBooking, httpBookingWithTimeout } from '@white-label-configuration/http';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';

import { PAYMENT_METHOD_STRATEGIES, PAYMENT_PROVIDERS, PaymentProvider } from '@white-label-configuration/constants';
import { myApp } from '@white-label-plugin/global-app-context';
import { datadogRum } from '@datadog/browser-rum';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { isSycurioFlow } from '@white-label-helper/is-sycurio-flow';


// @ts-ignore
httpBooking.onRequest((config) => {
  const session_id = datadogRum?.getInternalContext()?.session_id || null;
  if (isBookingPortalFlow()) {
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${myApp.$auth?.user['https://user_metadata/channel_token']}`;
  }
  config.headers['X-Datadog-Session-ID'] = session_id;
});

httpBookingWithTimeout.onRequest((config: AxiosRequestConfig) => {
  const sessionId = datadogRum?.getInternalContext()?.session_id ?? '';
  config.headers ??= {};

  if (isBookingPortalFlow()) {
    config.headers['Authorization'] = `Bearer ${myApp?.['$auth']?.user['https://user_metadata/channel_token']}`;
  }

  config.headers['X-Datadog-Session-ID'] = sessionId;
});

/**
 * * Constructs the request body for the /client-config endpoint based on the payment method.
 * @param paymentConfig - currency and amount information for Windcave and Advam payment methods.
 * @param route - the current Vue Router `Route` used to detect Sycurio flow.
 * @returns The request body object to be sent to the /client-config endpoint.
 */
const getRequestBody = (paymentConfig: ConfigOptions, route?: Route): RequestBody => {
  const merchantId = getAppPaymentMerchantId() || '';
  const paymentProvider = getAppPaymentProvider();

  const requestBody: RequestBody = {
    MerchantId: merchantId,
  };

  if (paymentProvider === PAYMENT_PROVIDERS.STRIPE) {
    const isSycurioConfig = route && isSycurioFlow(route as Route);
    requestBody.ClientConfigStrategyType = isSycurioConfig
      ? PAYMENT_METHOD_STRATEGIES.SYCURIO_PAYMENT
      : PAYMENT_METHOD_STRATEGIES.NEW_PAYMENT_ONLY;
    
    if (isSycurioConfig && paymentConfig) {
      requestBody.stripeCreateClientConfigRequest = {
        Amount: paymentConfig?.amount,
        Currency: paymentConfig?.currency,
      };
    }
  }

  if (paymentProvider === PAYMENT_PROVIDERS.WINDCAVE && paymentConfig) {
    requestBody.WindcaveCreateClientConfigRequest = {
      Amount: paymentConfig.amount,
      Currency: paymentConfig.currency,
      LanguageCode: getAppVariable('default_language'),
      PartnerId: getAppVariable('partner_id'),
    };
  }

  if (paymentProvider === PAYMENT_PROVIDERS.ADVAM && paymentConfig) {
    requestBody.AdvamCreateClientConfigRequest = {
      Amount: paymentConfig.amount,
      Currency: paymentConfig.currency,
      CountryCode: getAppVariable('poi.country.code'),
    };
  }

  return requestBody;
};

const MAX_RETRIES = 1;

/**
 * Fetches the client configuration from the payment gateway API.
 *
 * Retries the request up to MAX_RETRIES times in case of failure.
 *
 * @param config - Configuration options used to build the request body.
 * @param route - The current Vue Router `Route`, forwarded to Sycurio flow detection.
 * @returns client configuration result.
 *
 * @throws lastError if the request fails after all retries.
 */
export const getClientConfig = async (
  config: ConfigOptions,
  route?: Route
): Promise<ClientConfig['result']> => {
  const token = getAppVariable('channel.token');

  let attempt = 0;
  let lastError;

  while (attempt <= MAX_RETRIES) {
    try {
      const response = await httpBookingWithTimeout.post(
        '/payment-gateway/client-config', {
          headers: { Authorization: `Bearer ${token}` },
          body: getRequestBody(config, route),
        });

      return response.body.result;
    } catch (error) {
      lastError = error;
      attempt++;
    }
  }

  throw lastError ?? new Error('getClientConfig failed after retries');
};

/**
 * Handles the booking process for a pole by repeatedly checking the transaction status
 * and resolving or rejecting based on the response.
 *
 * @param sessionId - The unique identifier for the session/transaction being monitored.
 * @param interval - The interval duration in milliseconds to check the transaction status. Must be greater than 1.
 * @return  Returns an async function that checks the current status of the transaction.
 * It resolves with a success message if authorized or rejected with an error message for a failed status.
 */
export function poolingBooking(sessionId: string, interval: number) {
  if (interval < 1) {
    console.warn('Interval needs to be greater than 1');
  }

  async function checkCondition(resolve: (arg0: string) => void, reject: (arg0: Error) => void) {
    const windcaveStatusResponse = await httpBooking.get(`/payment/windcave:${sessionId}/status`);

    const body = windcaveStatusResponse.body.result;
    switch (body.status) {
      case 'Authorized': {
        resolve('Windcave: Authorized');
        break;
      }

      case 'Unknown': {
        setTimeout(checkCondition, interval);
        break;
      }

      default: {
        reject(Error(`status: ${body.status}, reason: ${body.statusReason}`));
        break;
      }
    }
  }

  return new Promise(checkCondition);
}

export interface VoidRequestBody {
  Id: string,
  PSP: PaymentProvider,
  MerchantId: string,
}

/**
 * Sends a void request to the Payment API via the Booking API.
 *
 * @param {VoidRequestBody} body - contains session id and PSP name.
 * @returns {Promise<AxiosRequestConfig>}
 */
export const handleVoidRequest = (body: VoidRequestBody): Promise<AxiosRequestConfig> => (
  httpBooking.post('payment-gateway/psptransactions/void', { body })
);
